import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Plus,
  X,
  Edit,
  Trash2,
  Save,
  Calendar as CalendarIcon,
  MessageSquare,
  Linkedin,
  Mail,
  Phone,
  Instagram,
  BarChart3,
  Building2,
  User,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'bloomberg_ib', label: 'Bloomberg IB', icon: BarChart3 },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
];

const EMPTY_FORM = { date: '', name: '', organization: '', location: '', mode: '' };

/* ── OutreachForm ── */
function OutreachForm({ initialData, isEditing, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({ ...initialData }));
  const [showCal, setShowCal] = useState(false);

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.date) { toast.error('Date is required'); return; }
    onSave(form);
  };

  return (
    <Card className="border-[#D4AF37]/30">
      <CardContent className="pt-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Date <span className="text-red-500">*</span>
            </Label>
            <Popover open={showCal} onOpenChange={setShowCal}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${!form.date ? 'text-muted-foreground' : ''}`}
                  data-testid="outreach-date-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date ? format(new Date(form.date + 'T12:00:00'), 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[60]">
                <Calendar
                  mode="single"
                  selected={form.date ? new Date(form.date + 'T12:00:00') : undefined}
                  onSelect={(d) => {
                    update('date', d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');
                    setShowCal(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g., John Smith"
              data-testid="outreach-name-input"
              autoFocus
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Organization <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              value={form.organization}
              onChange={(e) => update('organization', e.target.value)}
              placeholder="e.g., Goldman Sachs"
              data-testid="outreach-org-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Location <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              placeholder="e.g., Singapore"
              data-testid="outreach-location-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">
            Mode of Outreach <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <RadioGroup
            value={form.mode}
            onValueChange={(v) => update('mode', v)}
            className="flex flex-wrap gap-x-5 gap-y-3"
          >
            {MODES.map(({ value, label, icon: Icon }) => (
              <div key={value} className="flex items-center gap-1.5">
                <RadioGroupItem value={value} id={`mode-${value}`} />
                <Label htmlFor={`mode-${value}`} className="flex items-center gap-1 cursor-pointer text-sm">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onCancel} data-testid="outreach-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="outreach-save-btn">
            <Save className="w-4 h-4" />
            {isEditing ? 'Update' : 'Add Outreach'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── OutreachRow ── */
function OutreachRow({ item, onEdit, onDelete }) {
  const modeObj = MODES.find((m) => m.value === item.mode);
  const ModeIcon = modeObj?.icon || MessageSquare;

  return (
    <div
      className="flex items-center justify-between px-4 py-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
      data-testid={`outreach-row-${item.id}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <span className="text-sm text-slate-500 font-mono whitespace-nowrap w-24">
          {item.date ? format(new Date(item.date + 'T12:00:00'), 'dd MMM yyyy') : '—'}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="font-medium text-[#0F172A] truncate">{item.name}</span>
          </div>
          {item.organization && (
            <span className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
              <Building2 className="w-3 h-3" /> {item.organization}
              {item.location && <span className="text-slate-400 mx-1">·</span>}
              {item.location && <span>{item.location}</span>}
            </span>
          )}
          {!item.organization && item.location && (
            <span className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
              {item.location}
            </span>
          )}
        </div>
        {item.mode && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-full whitespace-nowrap">
            <ModeIcon className="w-3 h-3" /> {modeObj?.label || item.mode}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        <Button variant="ghost" size="icon" onClick={() => onEdit(item)} className="h-8 w-8 text-slate-400 hover:text-[#0F172A]" data-testid={`outreach-edit-${item.id}`}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-8 w-8 text-slate-400 hover:text-red-500" data-testid={`outreach-delete-${item.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function DailyOutreaches() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filterDate, setFilterDate] = useState('');

  const headers = useCallback(() => ({ Authorization: `Bearer ${tokenRef.current}` }), []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/daily-outreaches`, { headers: headers() });
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load outreaches');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = useMemo(() => {
    if (!filterDate) return items;
    return items.filter((i) => i.date === filterDate);
  }, [items, filterDate]);

  // Group by date
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((item) => {
      const key = item.date || 'No Date';
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    // Sort dates descending
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered]);

  const openAdd = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  const handleSave = async (formData) => {
    try {
      const h = { headers: headers() };
      if (editingItem) {
        await axios.put(`${API_URL}/api/daily-outreaches/${editingItem.id}`, formData, h);
        toast.success('Outreach updated');
      } else {
        await axios.post(`${API_URL}/api/daily-outreaches`, formData, h);
        toast.success('Outreach added');
      }
      closeForm();
      fetchItems();
    } catch {
      toast.error('Failed to save outreach');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/daily-outreaches/${deleteId}`, { headers: headers() });
      toast.success('Outreach deleted');
      setDeleteId(null);
      fetchItems();
    } catch {
      toast.error('Failed to delete outreach');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0F172A] text-white py-4 sm:py-6 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white hover:bg-slate-800 gap-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Contacts</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold">Daily Outreaches</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto py-6 sm:py-10 px-4 sm:px-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <p className="text-slate-500 text-sm">{items.length} total outreach{items.length !== 1 ? 'es' : ''}</p>
            {filterDate && (
              <button
                onClick={() => setFilterDate('')}
                className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#B08D28]"
              >
                <X className="w-3 h-3" /> Clear filter
              </button>
            )}
          </div>
          <Button onClick={openAdd} className="gap-2 bg-[#D4AF37] hover:bg-[#B08D28] text-white self-start" data-testid="add-outreach-btn">
            <Plus className="w-4 h-4" /> Add Outreach
          </Button>
        </div>

        {showForm && (
          <OutreachForm
            key={editingItem?.id || 'new'}
            initialData={editingItem ? { date: editingItem.date, name: editingItem.name, organization: editingItem.organization, location: editingItem.location || '', mode: editingItem.mode } : { ...EMPTY_FORM, date: filterDate || new Date().toISOString().slice(0, 10) }}
            isEditing={!!editingItem}
            onSave={handleSave}
            onCancel={closeForm}
          />
        )}

        {grouped.length > 0 ? (
          <div className="space-y-6">
            {grouped.map(([date, entries]) => (
              <div key={date}>
                <button
                  onClick={() => setFilterDate(filterDate === date ? '' : date)}
                  className="text-sm font-semibold text-[#0F172A] mb-2 flex items-center gap-2 hover:text-[#D4AF37] transition-colors"
                >
                  <CalendarIcon className="w-4 h-4" />
                  {date !== 'No Date' ? format(new Date(date + 'T12:00:00'), 'EEEE, dd MMMM yyyy') : 'No Date'}
                  <span className="text-xs text-slate-400 font-normal">({entries.length})</span>
                </button>
                <div className="space-y-2">
                  {entries.map((item) => (
                    <OutreachRow key={item.id} item={item} onEdit={openEdit} onDelete={setDeleteId} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-lg">
            {filterDate ? 'No outreaches on this date' : 'No outreaches yet. Click "Add Outreach" to start tracking.'}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Outreach</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
